import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { SpinScopeState } from '../../../Generated/SpinScopeState.ts'
import { authenticationSlice } from '../Authentication/AuthenticationSlice.js'

export type SpinStatusLoad = 'idle' | 'loading' | 'ready' | 'error'

/** A revalidation within this long of the last answer is skipped: mounts and focus events come in bursts. */
export const SPIN_STATE_REVALIDATE_AFTER_MS = 5_000

export interface ISpinStatusEntry {
  status: SpinStatusLoad
  /** Kept through a revalidation and a failed refetch, so consumers keep the last good answer. */
  data?: SpinScopeState
  /** What the answer was tagged with; sent back as `If-None-Match`. */
  etag?: string | null
  /** `Date.now()` of the last answer, fresh or 304. */
  validatedAt?: number
  /** Tags the fetch in flight; a response for an older request is dropped. */
  requestId?: string
  /** Mounted consumers; only watched scopes revalidate on focus. */
  watchers: number
}

export interface ISpinStatusState {
  byScope: Record<string, ISpinStatusEntry>
}

const initialState: ISpinStatusState = { byScope: {} }

/** The session-expiry handling both SPIN caches share; `entries` are their per-key records. */
export const pauseOnSessionExpiry = <Entry extends { status: SpinStatusLoad; requestId?: string }>(entries: Record<string, Entry>) => {
  for (const [key, entry] of Object.entries(entries)) {
    // The 401 never answers with the failure action, so a `loading` entry would block its key for good.
    if (entry.status === 'loading') {
      entries[key] = { ...entry, status: 'idle', requestId: undefined }
    }
  }
}

const entryOf = (state: ISpinStatusState, scopeId: string): ISpinStatusEntry => (state.byScope[scopeId] ??= { status: 'idle', watchers: 0 })

/**
 * The scope's SPIN state (`/spin/{scopeId}/state`): one entry per scope, fetched on the first mount, revalidated with
 * `If-None-Match` on later mounts and when the window regains focus or visibility.
 */
export const spinStatusSlice = createSlice({
  name: 'spinStatus',
  initialState,
  reducers: {
    /** A reload of the scope's SPIN data found it changed since it was cached, e.g. by the nightly clustering. */
    spinDataChanged: {
      reducer: (state: ISpinStatusState) => state,
      prepare: (payload: { scopeId: string }) => ({ payload })
    },
    /** Epic-only: fetches a scope never answered, revalidates an answered one; a fetch in flight is not repeated. */
    requestSpinState: {
      reducer: (state: ISpinStatusState) => state,
      prepare: (payload: { scopeId: string; force?: boolean }) => ({ payload })
    },
    /** Epic-only: the window is back in front; revalidates every watched scope. */
    revalidateWatchedSpinStates: (state: ISpinStatusState) => state,
    watchSpinState: (state, action: PayloadAction<{ scopeId: string }>) => {
      entryOf(state, action.payload.scopeId).watchers++
    },
    unwatchSpinState: (state, action: PayloadAction<{ scopeId: string }>) => {
      const entry = state.byScope[action.payload.scopeId]
      if (entry) {
        entry.watchers = Math.max(0, entry.watchers - 1)
      }
    },
    requestSpinStateStarted: (state, action: PayloadAction<{ scopeId: string; requestId: string }>) => {
      const entry = entryOf(state, action.payload.scopeId)
      entry.status = 'loading'
      entry.requestId = action.payload.requestId
    },
    requestSpinStateSuccess: (
      state,
      action: PayloadAction<{
        scopeId: string
        requestId: string
        validatedAt: number
        fresh?: { data: SpinScopeState; etag: string | null }
      }>
    ) => {
      const { scopeId, requestId, validatedAt, fresh } = action.payload
      const entry = state.byScope[scopeId]
      if (entry?.requestId !== requestId) {
        return
      }
      entry.status = 'ready'
      entry.requestId = undefined
      entry.validatedAt = validatedAt
      if (fresh) {
        entry.data = fresh.data
        entry.etag = fresh.etag
      }
    },
    requestSpinStateFailure: (state, action: PayloadAction<{ scopeId: string; requestId: string }>) => {
      const entry = state.byScope[action.payload.scopeId]
      if (entry?.requestId !== action.payload.requestId) {
        return
      }
      entry.status = 'error'
      entry.requestId = undefined
    }
  },
  extraReducers: (builder) => {
    builder.addCase(authenticationSlice.actions.authenticationExpired, (state) => {
      pauseOnSessionExpiry(state.byScope)
    })
  }
})
